import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { galleryTattoo, galleryPiercing, galleryStudio } from "@/lib/studio";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Normalize both gallery shapes (plain url strings, or {url, alt} objects)
// into a consistent {url, alt} form so the carousel and lightbox can share
// one code path regardless of which set is active.
function normalize(items, fallbackLabel) {
  return items.map((item, i) =>
    typeof item === "string"
      ? { url: item, alt: `${fallbackLabel} ${i + 1}` }
      : item
  );
}

function GalleryStrip({ items, onOpen, testId }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback((api) => {
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    return () => emblaApi?.off("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-3 md:-ml-4">
          {items.map((item, i) => (
            <div key={item.url + i} className="min-w-0 shrink-0 grow-0 basis-[78%] sm:basis-[46%] md:basis-[32%] lg:basis-[24%] pl-3 md:pl-4">
              <button
                onClick={() => onOpen(i)}
                className="group relative w-full aspect-[4/5] overflow-hidden border border-olive/20 hover:border-olive/80 transition-colors"
                data-testid={`${testId}-item-${i}`}
              >
                <img
                  src={item.url}
                  alt={item.alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors" />
                <div className="absolute bottom-3 left-3 text-[10px] tracking-[0.2em] uppercase text-cream opacity-0 group-hover:opacity-100 transition-opacity">
                  View
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Nav arrows — hidden on touch/mobile where swipe is the primary interaction */}
      <button
        onClick={() => emblaApi?.scrollPrev()}
        disabled={!canPrev}
        aria-label="Previous"
        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center border border-olive/40 bg-ink/80 text-cream hover:border-olive hover:bg-olive/10 transition-all disabled:opacity-0 disabled:pointer-events-none"
      >
        <ChevronLeft size={18} strokeWidth={1.6} />
      </button>
      <button
        onClick={() => emblaApi?.scrollNext()}
        disabled={!canNext}
        aria-label="Next"
        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center border border-olive/40 bg-ink/80 text-cream hover:border-olive hover:bg-olive/10 transition-all disabled:opacity-0 disabled:pointer-events-none"
      >
        <ChevronRight size={18} strokeWidth={1.6} />
      </button>
    </div>
  );
}

function Lightbox({ items, active, setActive }) {
  if (active === null) return null;
  const close = () => setActive(null);
  const next = () => setActive((i) => (i + 1) % items.length);
  const prev = () => setActive((i) => (i - 1 + items.length) % items.length);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/95 backdrop-blur-xl flex items-center justify-center p-4"
      data-testid="gallery-lightbox"
      onClick={close}
    >
      <button
        onClick={close}
        className="absolute top-5 right-5 text-cream/70 hover:text-cream"
        aria-label="Close"
        data-testid="gallery-lightbox-close"
      >
        <X size={28} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-5 md:left-10 text-cream/60 hover:text-cream p-3 border border-olive/30 hover:border-olive"
        aria-label="Previous"
        data-testid="gallery-lightbox-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <img
        src={items[active].url}
        alt={items[active].alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[90vw] object-contain border border-olive/30"
      />
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-5 md:right-10 text-cream/60 hover:text-cream p-3 border border-olive/30 hover:border-olive"
        aria-label="Next"
        data-testid="gallery-lightbox-next"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

export default function Gallery() {
  const [workTab, setWorkTab] = useState("tattoo");
  const [workActive, setWorkActive] = useState(null);
  const [studioActive, setStudioActive] = useState(null);

  const tattoo = normalize(galleryTattoo, "Tattoo work");
  const piercing = normalize(galleryPiercing, "Piercing work");
  const studio = normalize(galleryStudio, "Studio");
  const workItems = workTab === "tattoo" ? tattoo : piercing;

  const switchWorkTab = (t) => {
    setWorkTab(t);
    setWorkActive(null);
  };

  return (
    <section className="relative py-8 md:py-12 px-5 md:px-8" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="text-[10px] tracking-[0.34em] uppercase text-olive font-semibold mb-4">
              · Our work
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-cream leading-[1]">
              Selected pieces,
              <br />
              <span className="italic font-light font-shadows text-3xl md:text-4xl lg:text-5xl">fresh off the skin</span>
            </h2>
          </div>

          {/* Tattoo / Piercing tab toggle */}
          <div className="inline-flex border border-olive/30 w-fit" data-testid="gallery-tabs">
            <button
              onClick={() => switchWorkTab("tattoo")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 text-[10px] sm:text-[11px] tracking-[0.22em] uppercase font-medium transition-all ${
                workTab === "tattoo" ? "bg-olive text-cream" : "text-cream/60 hover:text-cream"
              }`}
              data-testid="gallery-tab-tattoo"
            >
              Tattoo
            </button>
            <button
              onClick={() => switchWorkTab("piercing")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 text-[10px] sm:text-[11px] tracking-[0.22em] uppercase font-medium transition-all border-l border-olive/30 ${
                workTab === "piercing" ? "bg-olive text-cream" : "text-cream/60 hover:text-cream"
              }`}
              data-testid="gallery-tab-piercing"
            >
              Piercing
            </button>
          </div>
        </div>

        {/* Tattoo / Piercing gallery */}
        <GalleryStrip items={workItems} onOpen={setWorkActive} testId="gallery-work" key={workTab} />

        {/* Studio gallery — always visible, separate from the tab switch above */}
        <div className="mt-14 md:mt-20">
          <div className="mb-6 md:mb-8">
            <div className="text-[10px] tracking-[0.34em] uppercase text-olive font-semibold mb-3">
              · Inside the studio
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-cream">
              The space itself.
            </h3>
          </div>
          <GalleryStrip items={studio} onOpen={setStudioActive} testId="gallery-studio" />
        </div>
      </div>

      <Lightbox items={workItems} active={workActive} setActive={setWorkActive} />
      <Lightbox items={studio} active={studioActive} setActive={setStudioActive} />
    </section>
  );
}
